import React from 'react';
import PropTypes from 'prop-types';
import { decodeTags, isValid } from '@brand/utils';
import './wow-box.scss';

// Mirrors DELTA_V_WOW_BOX_DEFAULT_VERDICT_HEADING /
// DELTA_V_WOW_BOX_DEFAULT_TAKEAWAYS_HEADING in the plugin
// (includes/wow-box-render-helpers.php) and the block.json attribute
// defaults. The compat payload already resolves a cleared heading to the
// default; these cover payloads cached before the headings shipped.
const DEFAULT_VERDICT_HEADING = 'Quick Verdict';
const DEFAULT_TAKEAWAYS_HEADING = 'Key Takeaways';

const headingText = (value, fallback) =>
  typeof value === 'string' && value.trim() !== '' ? value : fallback;

const WowBox = ({ data, className }) => {
  const {
    verdict,
    verdictHeading,
    takeaways = [],
    takeawaysHeading,
    isStale,
    hiddenFields = [],
  } = data;

  const isHidden = (field) => hiddenFields.includes(field);

  // Filter individual hidden items (e.g. "takeaways.2" hides 3rd takeaway).
  const filterItems = (items, section) =>
    items.filter((_, i) => !isHidden(`${section}.${i}`));

  const visibleTakeaways = isHidden('takeaways') ? [] : filterItems(takeaways, 'takeaways');

  const showVerdict = Boolean(verdict) && !isHidden('verdict');
  const showTakeaways = isValid(visibleTakeaways);

  // Don't render ungenerated, empty, or fully-hidden wow boxes. Stale payloads
  // may still carry pros/cons; those sections were removed from generation and
  // from both render paths (A2), so they never count as content.
  if (!showVerdict && !showTakeaways) {
    return null;
  }

  return (
    <div className={`wow-box ${className || ''}`}>
      {showVerdict && (
        <>
          <h2 className="wow-box__heading">
            {headingText(verdictHeading, DEFAULT_VERDICT_HEADING)}
          </h2>
          <p className="wow-box__verdict" dangerouslySetInnerHTML={{ __html: decodeTags(verdict) }} />
        </>
      )}

      {showTakeaways && (
        <>
          <h2 className="wow-box__heading">
            {headingText(takeawaysHeading, DEFAULT_TAKEAWAYS_HEADING)}
          </h2>
          <ul className="wow-box__takeaways">
            {visibleTakeaways.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: decodeTags(item) }} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

WowBox.propTypes = {
  data: PropTypes.shape({
    verdict: PropTypes.string,
    verdictHeading: PropTypes.string,
    takeaways: PropTypes.arrayOf(PropTypes.string),
    takeawaysHeading: PropTypes.string,
    isStale: PropTypes.bool,
    hiddenFields: PropTypes.arrayOf(PropTypes.string),
  }),
  className: PropTypes.string,
};

export default WowBox;
